// ============================================
// CATALOGOADMIN.JS — Panel del catálogo Beep definitivo
// 2 tabs: Cableado · Mano de obra
// ============================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authHeaders } from './utils/storage';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
} from './components/helpersClientes';
import SelectorMoneda, { useMoneda } from './components/SelectorMoneda';
import AgregarAListaModal from './components/AgregarAListaModal';
import SubirImagen from './components/SubirImagen';
import { tokens } from './styles/tokens';
import {
  Package, Pencil, Trash2, Plus, X, ImageOff, FolderOpen,
  Cable, Wrench, ClipboardList, Cctv, DoorOpen, Siren, Network,
} from 'lucide-react';
import { useBorrador, AvisoRescate } from './utils/borradorFormulario';
import { API_BASE } from './config/api';
import SelectorFamilia from './components/SelectorFamilia';
import { CampoPrecio } from './components/LineaPrecioEditable';
import { fmtNumDinero } from './utils/dinero';
import BuscadorInteligente, { useBuscadorInteligente } from './components/BuscadorInteligente';
import FiltroRubro, { useFiltroRubro, AvisoVacioRubro } from './components/FiltroRubro';

const NIVEL_CABLEADO = [
  { id: 'precio_aparente',           label: 'Aparente',          color: tokens.success },
  { id: 'precio_tuberia_guiada',     label: 'Tubería guiada',    color: tokens.accent },
  { id: 'precio_tuberia_no_guiada',  label: 'Tubería no guiada', color: tokens.warning },
  { id: 'precio_tuberia_saturada',   label: 'Tubería saturada',  color: tokens.danger },
];

const NIVEL_INSTALACION = [
  { id: 'basica',   label: 'Básica',   color: tokens.success },
  { id: 'estandar', label: 'Estándar', color: tokens.accent },
  { id: 'altura',   label: 'Altura',   color: tokens.warning },
  { id: 'compleja', label: 'Compleja', color: tokens.danger },
];

// Punto de nivel: circulo de color CSS (reemplaza los emojis de bolita).
function PuntoNivel({ color, size = 10 }) {
  return <span style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', background: color, flexShrink: 0, verticalAlign: 'middle' }} />;
}

// Icono de categoria de mano de obra: mapa por nombre -> Lucide, default Wrench. Hereda currentColor.
function IconoManoObra({ nombre, size = 22 }) {
  const n = String(nombre || '').toLowerCase();
  let Ico = Wrench;
  if (n.includes('cctv') || n.includes('camara') || n.includes('video')) Ico = Cctv;
  else if (n.includes('acceso') || n.includes('puerta')) Ico = DoorOpen;
  else if (n.includes('alarma')) Ico = Siren;
  else if (n.includes('red')) Ico = Network;
  return <Ico size={size} strokeWidth={2} />;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export default function CatalogoAdmin() {
  const [tab, setTab] = useState('cableado');

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS, padding: 20, background: COLOR_FONDO, minHeight: '100%' }}>
      <div style={{
        marginBottom: 18, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>
            <Package size={18} strokeWidth={2} /> Mis Productos
          </div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4, lineHeight: 1.5 }}>
            Tu catálogo propio multi-proveedor (Beep y otros proveedores). Tres tipos: Cableado
            (material + mano de obra por metro) · Instalaciones · Servicios · Productos.
            Precios guardados en MXN — usa el selector para ver en USD.
          </div>
        </div>
        <SelectorMoneda />
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${COLOR_BORDE}` }}>
        {[
          { id: 'cableado',  label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Cable size={14} strokeWidth={2} /> Cableado</span> },
          { id: 'servicios', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Wrench size={14} strokeWidth={2} /> Servicios e Instalaciones</span> },
          { id: 'productos', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Package size={14} strokeWidth={2} /> Productos</span> },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 16px', fontSize: 13, fontFamily: PP, cursor: 'pointer',
              background: 'transparent', border: 'none',
              color: tab === t.id ? COLOR_OSCURO : COLOR_GRIS_CLARO,
              fontWeight: tab === t.id ? 700 : 500,
              borderBottom: tab === t.id ? `3px solid ${COLOR_PRINCIPAL}` : '3px solid transparent',
              marginBottom: -1,
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'cableado' && (
        <ListaProductos
          tipo="cable"
          titulo="Cables (instalacion por metro). Cada cable con sus niveles como presentaciones."
          textoBotonNuevo="Nuevo cable"
          textoVacio='Aun no hay cables. Crea el primero con "Nuevo cable".'
          confirmarBorrar={(p) => `Borrar el cable "${p.nombre}"? Se puede reactivar despues.`}
        />
      )}
      {tab === 'servicios' && (
        <ListaProductos
          tipo="servicio"
          titulo="Servicios propios (instalaciones y mano de obra). Crea cada servicio con sus presentaciones/niveles como variantes."
          textoBotonNuevo="Nuevo servicio"
          textoVacio='Aun no hay servicios. Crea el primero con "Nuevo servicio".'
          confirmarBorrar={(p) => `Borrar el servicio "${p.nombre}"? Se puede reactivar despues.`}
        />
      )}
      {tab === 'productos' && (
        <ListaProductos
          tipo="producto"
          titulo="Productos propios (alta manual, multi-proveedor). Aparecen en La Tienda."
          textoBotonNuevo="Nuevo producto"
          textoVacio='Aun no hay productos propios. Crea el primero con "Nuevo producto".'
          confirmarBorrar={(p) => `Borrar "${p.nombre}"? Se puede reactivar despues.`}
        />
      )}
    </div>
  );
}

// ============================================
// TAB 3: PRODUCTOS — CRUD de beep_productos (D1)
// ============================================


function fmtPrecio(n) {
  return '$' + Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' MXN';
}

// Rango de precios de un producto variable: "$X" si min==max, "desde $X" si difieren.
function rangoPrecio(variantes) {
  const precios = (variantes || []).map(v => Number(v.precio_venta_mxn) || 0);
  if (precios.length === 0) return fmtPrecio(0);
  const min = Math.min(...precios);
  const max = Math.max(...precios);
  return min === max ? fmtPrecio(min) : `desde ${fmtPrecio(min)}`;
}

const btnIconoProd = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 44, minHeight: 44,
  background: tokens.surface, border: `1px solid ${tokens.border}`,
  borderRadius: 8, cursor: 'pointer', color: tokens.text, fontFamily: PP,
};

const CATEGORIAS_PRODUCTO = [
  'BeepTV',
  'Camaras / CCTV', 'Grabadoras (DVR/NVR)', 'Cables y conectores', 'Redes',
  'Alarmas', 'Control de acceso', 'Videoportero', 'Energia (fuentes/baterias/UPS)',
  'Herramientas', 'Accesorios', 'Otros',
];

function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
function gananciaDe(costo, venta) {
  const c = Number(costo) || 0;
  const v = Number(venta) || 0;
  return c > 0 ? round2(((v - c) / c) * 100) : 0;
}

// Lo que el buscador mira de cada item de Mis Productos.
function candidatoDeProducto(p) {
  return { id: p.id, titulo: [p.nombre, p.categoria, p.familia].filter(Boolean).join(' '), marca: p.proveedor || '', modelo: p.sku || '' };
}

// Lista reusable de items de beep_productos. tipo='producto' o 'servicio' (mismo CRUD,
// distinto filtro y textos visibles). El modal "Nuevo" hereda el tipo de la pestana.
function ListaProductos({
  tipo = 'producto',
  titulo = 'Productos propios.',
  textoBotonNuevo = 'Nuevo producto',
  textoVacio = 'Aun no hay items. Crea el primero.',
  confirmarBorrar = (p) => `Borrar "${p.nombre}"? Se puede reactivar despues.`,
}) {
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // null | { producto } (producto=null => nuevo)
  const [guardando, setGuardando] = useState(false);
  // BUSCADOR-UNICO (2026-10-12): el motor de La Tienda sobre los items de esta pestana.
  const busq = useBuscadorInteligente(productos, candidatoDeProducto);
  // RUBROS (2026-10-16): filtro por rubro encima del buscador; los sin familia salen como "Material general".
  const rubro = useFiltroRubro(busq.visibles);

  // Categorias que YA usan los productos cargados (para que una creada antes reaparezca en el select).
  const categoriasUsadas = useMemo(
    () => [...new Set(productos.map(p => p.categoria).filter(Boolean))],
    [productos]
  );

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      // SEGURIDAD-ENDPOINTS-ABIERTOS (2026-09-18): el catalogo ya exige sesion (devolvia el costo
      // interno de todos los productos a cualquiera con la URL).
      const r = await fetch(`${API_BASE}/api/catalogo/productos?activo=true&tipo=${tipo}`, { headers: authHeaders() }).then(x => x.json());
      if (r.ok) setProductos(r.data || []);
      else setError(r.error || 'No se pudo cargar el catalogo');
    } catch (e) {
      setError('Error de conexion al cargar productos');
    } finally {
      setCargando(false);
    }
  }, [tipo]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = useCallback(async (body, id) => {
    setGuardando(true);
    try {
      const url = id
        ? `${API_BASE}/api/catalogo/productos/${id}`
        : `${API_BASE}/api/catalogo/productos`;
      const r = await fetch(url, {
        method: id ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(x => x.json());
      if (r.ok) {
        setModal(null);
        await cargar();
      } else {
        alert(r.error || 'No se pudo guardar');
      }
    } catch (e) {
      alert('Error de conexion al guardar');
    } finally {
      setGuardando(false);
    }
  }, [cargar]);

  const borrar = useCallback(async (p) => {
    if (!window.confirm(confirmarBorrar(p))) return;
    try {
      const r = await fetch(`${API_BASE}/api/catalogo/productos/${p.id}`, { method: 'DELETE', headers: { ...authHeaders() } }).then(x => x.json());
      if (r.ok) await cargar();
      else alert(r.error || 'No se pudo borrar');
    } catch (e) {
      alert('Error de conexion al borrar');
    }
  }, [cargar, confirmarBorrar]);

  return (
    <div style={{ fontFamily: PP }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: tokens.textSecondary }}>
          {titulo}
        </div>
        <button
          onClick={() => setModal({ producto: null })}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: tokens.primary, color: tokens.textOnPrimary, border: 'none',
            borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 700,
            cursor: 'pointer', fontFamily: PP, minHeight: 44,
          }}
        ><Plus size={16} strokeWidth={2} /> {textoBotonNuevo}</button>
      </div>

      {cargando && (
        <div style={{ padding: 40, textAlign: 'center', color: tokens.textMuted, fontSize: 14 }}>
          Cargando productos...
        </div>
      )}

      {error && !cargando && (
        <div style={{ padding: 16, borderRadius: 10, background: tokens.dangerSoft, color: tokens.danger, fontSize: 13 }}>
          {error}
        </div>
      )}

      {!cargando && !error && productos.length === 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 10, padding: '56px 16px', textAlign: 'center',
        }}>
          <FolderOpen size={36} color={tokens.textMuted} strokeWidth={1.5} />
          <div style={{ color: tokens.textMuted, fontSize: 14 }}>
            {textoVacio}
          </div>
        </div>
      )}

      {!cargando && productos.length > 0 && (
        <>
          <BuscadorInteligente busq={busq} placeholder="Buscar (nombre, proveedor, SKU o categoria)" />
          <FiltroRubro filtro={rubro} />
          <AvisoVacioRubro filtro={rubro} busqueda={busq.q} />
        </>
      )}

      {!cargando && productos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rubro.visibles.map(p => (
            <div key={p.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              background: tokens.surface, border: `1px solid ${tokens.borderSubtle}`,
              borderRadius: 12, padding: 12,
            }}>
              <div style={{
                width: 56, height: 56, flexShrink: 0, borderRadius: 8,
                background: tokens.surfaceSubtle, overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {p.imagen_url
                  ? <img src={p.imagen_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                  : <ImageOff size={22} color={tokens.textMuted} strokeWidth={1.5} aria-label="Sin imagen" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: tokens.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.nombre}
                </div>
                <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>
                  {p.categoria || 'Otros'}{p.sku ? ` · ${p.sku}` : ''}{p.proveedor ? ` · ${p.proveedor}` : ''}
                </div>
                {p.tiene_variantes && Array.isArray(p.variantes) && p.variantes.length > 0 ? (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 700, color: tokens.text, marginTop: 4 }}>
                      {rangoPrecio(p.variantes)}
                    </div>
                    <div style={{ fontSize: 11, color: tokens.textMuted, marginTop: 1 }}>
                      Variable · {p.variantes.length} presentacion{p.variantes.length === 1 ? '' : 'es'}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 14, fontWeight: 700, color: tokens.text, marginTop: 4 }}>
                    {fmtPrecio(p.precio_venta_mxn)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => setModal({ producto: p })} aria-label="Editar" style={btnIconoProd}>
                  <Pencil size={16} strokeWidth={2} />
                </button>
                <button onClick={() => borrar(p)} aria-label="Borrar" style={{ ...btnIconoProd, color: tokens.danger }}>
                  <Trash2 size={16} strokeWidth={2} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ModalProducto
          producto={modal.producto}
          tipoDefault={tipo}
          categoriasUsadas={categoriasUsadas}
          guardando={guardando}
          onCerrar={() => setModal(null)}
          onGuardar={guardar}
        />
      )}
    </div>
  );
}

// Exportado ademas de uso interno: La Tienda lo reusa para "Duplicar a Mis Productos"
// (mismo modal, pre-llenado con datos Syscom). NO duplicar este componente.
export function ModalProducto({ producto, tipoDefault = 'producto', categoriasUsadas = [], onGuardar, onCerrar, guardando }) {
  const [modoNueva, setModoNueva] = useState(false); // true = escribiendo una categoria nueva
  const [form, setForm] = useState({
    nombre: producto?.nombre || '',
    descripcion: producto?.descripcion || '',
    precio_venta_mxn: producto?.precio_venta_mxn != null ? String(producto.precio_venta_mxn) : '0',
    costo_interno_mxn: producto?.costo_interno_mxn != null ? String(producto.costo_interno_mxn) : '0',
    // ganancia% es solo el lever de la calculadora; NO se guarda en DB.
    ganancia: producto ? String(gananciaDe(producto.costo_interno_mxn, producto.precio_venta_mxn)) : '30',
    // "Mis Productos" multi-proveedor: default 'Beep' en alta (Beep es UN proveedor mas).
    proveedor: producto?.proveedor || 'Beep',
    sku: producto?.sku || '',
    categoria: producto?.categoria || 'Otros',
    // CATALOGO-FAMILIAS (2026-09-24): de que TECNOLOGIA es. Es un eje DISTINTO de
    // `categoria`, que dice el tipo de servicio ("Instalación", "Otros"). Sin esto,
    // "Mantenimiento de CCTV" (categoria 'Otros') no se podia agrupar con lo de CCTV.
    familia: producto?.familia || '',
    imagen_url: producto?.imagen_url || null,
    activo: producto?.activo != null ? producto.activo : true,
    orden: producto?.orden != null ? String(producto.orden) : '0',
    // tipo: del item al editar, o de la pestana al crear.
    tipo: producto?.tipo || tipoDefault,
    // unidad: 'pieza' por defecto, 'metro' para cables (o lo que traiga el item).
    unidad: producto?.unidad || (tipoDefault === 'cable' ? 'metro' : 'pieza'),
    // moneda_base: en que moneda se capturan los precios (producto + variantes).
    moneda_base: producto?.moneda_base === 'USD' ? 'USD' : 'MXN',
    // STOCK-MIS-PRODUCTOS: inventario propio. '' = sin registrar (null en BD);
    // productos existentes sin dato quedan vacios, altas nuevas arrancan en 0.
    stock: producto?.stock != null ? String(producto.stock) : (producto ? '' : '0'),
    // GARANTIAS: meses de garantia del producto propio (obligatorio, default 12).
    garantia_meses: producto?.garantia_meses != null ? String(producto.garantia_meses) : '12',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Borrador local (TANDA 2): el mismo mecanismo de Editar expediente, clave usuario:pantalla:registro.
  const bor = useBorrador('catalogo-producto', producto?.id || 'nuevo', form, setForm, { omitir: ['imagen_url'] });

  // Proveedores ya usados (para el combo editable que RECUERDA + permite escribir nuevo).
  const [proveedoresUsados, setProveedoresUsados] = useState([]);
  useEffect(() => {
    fetch(`${API_BASE_CLI}/api/catalogo/productos/proveedores`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { if (d && d.ok) setProveedoresUsados(d.data || []); })
      .catch(() => {});
  }, []);

  // ---- Variantes (presentaciones) ----
  // tiene_variantes ON: el producto es variable; se editan N filas etiqueta+precios.
  // Precarga ON con sus variantes al editar; OFF con una fila vacia por defecto.
  const [tieneVariantes, setTieneVariantes] = useState(producto?.tiene_variantes === true);
  const [variantes, setVariantes] = useState(() => {
    if (producto?.tiene_variantes && Array.isArray(producto.variantes) && producto.variantes.length > 0) {
      return producto.variantes.map(v => ({
        etiqueta: v.etiqueta || '',
        precio_venta_mxn: v.precio_venta_mxn != null ? String(v.precio_venta_mxn) : '0',
        costo_interno_mxn: v.costo_interno_mxn != null ? String(v.costo_interno_mxn) : '0',
      }));
    }
    return [{ etiqueta: '', precio_venta_mxn: '0', costo_interno_mxn: '0' }];
  });
  const setVariante = (i, k, v) => setVariantes(arr => arr.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const agregarVariante = () => setVariantes(arr => [...arr, { etiqueta: '', precio_venta_mxn: '0', costo_interno_mxn: '0' }]);
  const quitarVariante = (i) => setVariantes(arr => arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr);

  // Calculadora de precio (doble via). costo<->ganancia<->venta. utilidad = venta - costo.
  const numCosto = Number(form.costo_interno_mxn) || 0;
  const numVenta = Number(form.precio_venta_mxn) || 0;
  const utilidad = numVenta - numCosto;

  const setCosto = (v) => {
    const g = Number(form.ganancia) || 0;
    setForm(f => ({ ...f, costo_interno_mxn: v, precio_venta_mxn: String(round2((Number(v) || 0) * (1 + g / 100))) }));
  };
  const setGanancia = (v) => {
    const c = Number(form.costo_interno_mxn) || 0;
    setForm(f => ({ ...f, ganancia: v, precio_venta_mxn: String(round2(c * (1 + (Number(v) || 0) / 100))) }));
  };
  const setVenta = (v) => {
    const c = Number(form.costo_interno_mxn) || 0;
    setForm(f => ({ ...f, precio_venta_mxn: v, ganancia: String(c > 0 ? gananciaDe(c, v) : 0) }));
  };

  // Opciones del select: lista fija + categorias ya usadas por productos + la del producto editado, sin duplicados.
  const opcionesCategoria = [...new Set([
    ...CATEGORIAS_PRODUCTO,
    ...categoriasUsadas,
    ...(producto?.categoria ? [producto.categoria] : []),
  ].filter(Boolean))];

  const submit = () => {
    if (!form.nombre || !form.nombre.trim()) {
      alert('El nombre es obligatorio');
      return;
    }
    const base = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion || '',
      proveedor: (form.proveedor && form.proveedor.trim()) || 'Beep',
      sku: form.sku ? form.sku.trim() : null,
      categoria: (form.categoria && form.categoria.trim()) || 'Otros',
      imagen_url: form.imagen_url || null,
      activo: form.activo !== false,
      orden: Number(form.orden) || 0,
      tipo: (form.tipo === 'servicio' || form.tipo === 'cable') ? form.tipo : 'producto',
      familia: form.familia || null,
      unidad: form.unidad === 'metro' ? 'metro' : 'pieza',
      moneda_base: form.moneda_base === 'USD' ? 'USD' : 'MXN',
      // Inventario propio: vacio = null (sin registrar); si hay numero, entero >= 0.
      stock: form.stock === '' ? null : Math.max(0, Math.trunc(Number(form.stock) || 0)),
      garantia_meses: Math.max(1, Math.trunc(Number(form.garantia_meses) || 0)) || 12,
    };
    if (tieneVariantes) {
      // Cada variante necesita etiqueta no vacia. El backend forzara el precio del producto al MIN.
      for (const v of variantes) {
        if (!v.etiqueta || !v.etiqueta.trim()) {
          alert('Cada variante necesita una etiqueta');
          return;
        }
      }
      const vlist = variantes.map((v, idx) => ({
        etiqueta: v.etiqueta.trim(),
        precio_venta_mxn: Number(v.precio_venta_mxn) || 0,
        costo_interno_mxn: Number(v.costo_interno_mxn) || 0,
        orden: idx,
      }));
      bor.limpiar(); // guardado: el borrador ya no hace falta
      onGuardar({ ...base, tiene_variantes: true, variantes: vlist }, producto?.id);
    } else {
      bor.limpiar(); // guardado: el borrador ya no hace falta
      onGuardar({
        ...base,
        tiene_variantes: false,
        precio_venta_mxn: Number(form.precio_venta_mxn) || 0,
        costo_interno_mxn: Number(form.costo_interno_mxn) || 0,
      }, producto?.id);
    }
  };

  const lbl = { fontSize: 12, fontWeight: 600, color: tokens.textSecondary, marginBottom: 4 };

  return (
    <div
      onClick={bor.confirmarCierre(onCerrar)}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: tokens.surface, borderRadius: 14, width: '100%', maxWidth: 520,
          maxHeight: 'calc(var(--beep-vh) * 92)', overflowY: 'auto', fontFamily: PP, color: tokens.text,
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${tokens.borderSubtle}`,
          position: 'sticky', top: 0, background: tokens.surface, zIndex: 1,
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: tokens.text }}>
            {producto ? 'Editar producto' : 'Nuevo producto'}
          </div>
          <button onClick={bor.confirmarCierre(onCerrar)} aria-label="Cerrar" style={{ ...btnIconoProd, border: 'none', background: 'transparent', color: tokens.textMuted }}>
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <AvisoRescate bor={bor} />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={lbl}>Tipo</div>
              <select
                style={estiloInput}
                value={form.tipo}
                onChange={e => {
                  const nuevoTipo = e.target.value;
                  // Sugerir 'metro' al pasar a cable si la unidad seguia en 'pieza'.
                  setForm(f => ({
                    ...f,
                    tipo: nuevoTipo,
                    unidad: (nuevoTipo === 'cable' && f.unidad === 'pieza') ? 'metro' : f.unidad,
                  }));
                }}
              >
                <option value="producto">Producto</option>
                <option value="servicio">Servicio</option>
                <option value="cable">Cable</option>
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={lbl}>Unidad</div>
              <select style={estiloInput} value={form.unidad} onChange={e => set('unidad', e.target.value)}>
                <option value="pieza">Pieza</option>
                <option value="metro">Metro</option>
              </select>
            </div>
          </div>
          <div>
            <div style={lbl}>Nombre *</div>
            <input style={estiloInput} value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre del producto" />
          </div>
          <div>
            <div style={lbl}>Descripcion</div>
            <textarea style={{ ...estiloInput, minHeight: 110, resize: 'vertical' }} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} />
          </div>
          {/* CATALOGO-FAMILIAS: de que tecnologia es este servicio. Decide si sale arriba
              al cotizar desde un expediente de esa tecnologia. Vacio = se deduce del nombre. */}
          <SelectorFamilia
            etiqueta="Tipo de trabajo (familia)"
            valor={form.familia}
            onChange={(v) => set('familia', v || '')}
            ayuda={<div style={{ fontSize: 11.5, color: tokens.textMuted, marginTop: 4, lineHeight: 1.4 }}>
              Distinto de la categoria: la categoria dice que CLASE de servicio es
              ("Instalación", "Otros"); esto dice de que TECNOLOGIA. De aqui sale que
              servicios se ofrecen primero al cotizar desde un expediente.
            </div>}
          />
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
            background: tokens.surfaceSubtle, borderRadius: 12, padding: '12px 14px',
          }}>
            <input type="checkbox" checked={tieneVariantes} onChange={e => setTieneVariantes(e.target.checked)} style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: tokens.text }}>Tiene variantes (presentaciones)?</div>
              <div style={{ fontSize: 11, color: tokens.textMuted, marginTop: 2, lineHeight: 1.4 }}>
                Activa si el producto tiene varias presentaciones (tamano, color, etc.) con precios distintos.
              </div>
            </div>
          </label>

          {/* W1.2: moneda de captura (aplica al producto y a TODAS sus variantes). */}
          <div style={{
            background: tokens.surfaceSubtle, borderRadius: 12, padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: tokens.textSecondary }}>Moneda de captura</div>
            <div style={{ display: 'inline-flex', border: `1px solid ${tokens.border}`, borderRadius: 99, overflow: 'hidden' }}>
              {['MXN', 'USD'].map(m => (
                <button
                  key={m} type="button" onClick={() => set('moneda_base', m)}
                  style={{
                    padding: '6px 16px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer',
                    background: form.moneda_base === m ? tokens.primary : 'transparent',
                    color: form.moneda_base === m ? '#fff' : tokens.text,
                  }}
                >{m}</button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: tokens.textMuted }}>
              Los precios de abajo se capturan en <b>{form.moneda_base}</b>
              {form.moneda_base === 'USD' ? ' · se convierten a MXN con el TC de venta.' : '.'}
            </div>
          </div>

          {!tieneVariantes && (
          <div style={{ background: tokens.surfaceSubtle, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: tokens.textSecondary }}>Calculadora de precio ({form.moneda_base})</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={lbl}>Precio de compra ({form.moneda_base})</div>
                <CampoPrecio style={estiloInput} value={form.costo_interno_mxn} onChange={e => setCosto(e.target.value)} />
              </div>
              <div style={{ flex: 1, minWidth: 100 }}>
                <div style={lbl}>Ganancia %</div>
                <input style={estiloInput} type="number" step="0.1" value={form.ganancia} onChange={e => setGanancia(e.target.value)} />
              </div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={lbl}>Precio de venta ({form.moneda_base})</div>
                <CampoPrecio style={estiloInput} value={form.precio_venta_mxn} onChange={e => setVenta(e.target.value)} />
              </div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              background: tokens.surface, borderRadius: 8, padding: '8px 12px',
              border: `1px solid ${tokens.borderSubtle}`,
            }}>
              <span style={{ fontSize: 12, color: tokens.textSecondary }}>Utilidad</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: utilidad >= 0 ? tokens.success : tokens.danger }}>
                {fmtPrecio(utilidad)} <span style={{ fontSize: 11, color: tokens.textMuted, fontWeight: 600 }}>({round2(form.ganancia)}%)</span>
              </span>
            </div>
          </div>
          )}

          {tieneVariantes && (
          <div style={{ background: tokens.surfaceSubtle, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: tokens.textSecondary }}>Variantes (presentaciones) · precios en {form.moneda_base}</div>
            {variantes.map((v, i) => {
              const util = (Number(v.precio_venta_mxn) || 0) - (Number(v.costo_interno_mxn) || 0);
              const hayCompra = (Number(v.costo_interno_mxn) || 0) > 0;
              return (
                <div key={i} style={{
                  background: tokens.surface, border: `1px solid ${tokens.borderSubtle}`,
                  borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={lbl}>Etiqueta</div>
                      <input style={estiloInput} value={v.etiqueta} onChange={e => setVariante(i, 'etiqueta', e.target.value)} placeholder="ej. 200ml / Altura" />
                    </div>
                    <button
                      onClick={() => quitarVariante(i)}
                      aria-label="Quitar variante"
                      disabled={variantes.length <= 1}
                      style={{ ...btnIconoProd, color: tokens.danger, opacity: variantes.length <= 1 ? 0.4 : 1 }}
                    ><Trash2 size={16} strokeWidth={2} /></button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 110 }}>
                      <div style={lbl}>Precio de venta</div>
                      <CampoPrecio style={estiloInput} value={v.precio_venta_mxn} onChange={e => setVariante(i, 'precio_venta_mxn', e.target.value)} />
                    </div>
                    <div style={{ flex: 1, minWidth: 110 }}>
                      <div style={lbl}>Precio de compra</div>
                      <CampoPrecio style={estiloInput} value={v.costo_interno_mxn} onChange={e => setVariante(i, 'costo_interno_mxn', e.target.value)} placeholder="Opcional" />
                    </div>
                  </div>
                  {hayCompra && (
                    <div style={{ fontSize: 12, fontWeight: 600, color: util >= 0 ? tokens.success : tokens.danger }}>
                      Utilidad: {fmtPrecio(util)}
                    </div>
                  )}
                </div>
              );
            })}
            <button onClick={agregarVariante} style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: tokens.surface, color: tokens.text, border: `1px dashed ${tokens.border}`,
              borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: PP, minHeight: 44,
            }}><Plus size={16} strokeWidth={2} /> Agregar variante</button>
          </div>
          )}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={lbl}>Proveedor</div>
              {/* Combo editable: elige uno ya usado (datalist) O escribe uno nuevo. */}
              <input
                style={estiloInput}
                list="lista-proveedores-mis-productos"
                value={form.proveedor}
                onChange={e => set('proveedor', e.target.value)}
                placeholder="Beep"
              />
              <datalist id="lista-proveedores-mis-productos">
                {proveedoresUsados.map(p => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={lbl}>SKU</div>
              <input style={estiloInput} value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="Opcional" />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={lbl}>Inventario (stock)</div>
              <input
                style={estiloInput}
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={form.stock}
                onChange={e => set('stock', e.target.value)}
                placeholder="Sin registrar"
              />
            </div>
            <div>
              <div style={lbl}>Garantia (meses) *</div>
              <input
                style={estiloInput}
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={form.garantia_meses}
                onChange={e => set('garantia_meses', e.target.value)}
                placeholder="12"
              />
            </div>
          </div>
          <div>
            <div style={lbl}>Categoria</div>
            <select
              style={estiloInput}
              value={modoNueva ? '__nueva__' : form.categoria}
              onChange={e => {
                const v = e.target.value;
                if (v === '__nueva__') { setModoNueva(true); set('categoria', ''); }
                else { setModoNueva(false); set('categoria', v); }
              }}
            >
              {opcionesCategoria.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="__nueva__">+ Escribir otra...</option>
            </select>
            {modoNueva && (
              <input
                style={{ ...estiloInput, marginTop: 8 }}
                value={form.categoria}
                onChange={e => set('categoria', e.target.value)}
                placeholder="Nombre de la categoria nueva"
                autoFocus
              />
            )}
          </div>
          <div>
            <div style={lbl}>Imagen</div>
            <SubirImagen
              value={form.imagen_url}
              folder="beep-os/productos"
              label=""
              aspectRatio="square"
              onChange={(url) => set('imagen_url', url)}
              onDelete={() => set('imagen_url', null)}
            />
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: tokens.text }}>
              <input type="checkbox" checked={form.activo !== false} onChange={e => set('activo', e.target.checked)} />
              Activo (visible)
            </label>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: tokens.textSecondary }}>Orden</span>
              <input style={{ ...estiloInput, width: 80 }} type="number" value={form.orden} onChange={e => set('orden', e.target.value)} />
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '14px 20px', borderTop: `1px solid ${tokens.borderSubtle}`,
          position: 'sticky', bottom: 0, background: tokens.surface,
        }}>
          <button onClick={bor.confirmarCierre(onCerrar)} disabled={guardando} style={{
            background: tokens.surface, color: tokens.textSecondary, border: `1px solid ${tokens.border}`,
            borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: PP, minHeight: 44,
          }}>Cancelar</button>
          <button onClick={submit} disabled={guardando} style={{
            background: tokens.primary, color: tokens.textOnPrimary, border: 'none',
            borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: PP, minHeight: 44,
            opacity: guardando ? 0.6 : 1,
          }}>{guardando ? 'Guardando...' : (producto?.id ? 'Guardar cambios' : 'Crear producto')}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// TAB 1: CABLEADO — tabla inline-editable con batch save
// ============================================

function TabCableado() {
  const [cables, setCables] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalBatch, setModalBatch] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  // el modalAgregar individual se eliminó: ahora se usa modalBatch
  const [sucio, setSucio] = useState(new Set());

  const mostrarToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(null), 2500);
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/catalogo/cableado?todos=true`).then(x => x.json());
      if (r.ok) {
        setCables(r.cables || []);
        setSucio(new Set());
      }
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = (id, campo, valor) => {
    setCables(lista => lista.map(c => c.id === id ? { ...c, [campo]: valor } : c));
    setSucio(s => new Set(s).add(id));
  };

  const guardarTodo = async () => {
    if (sucio.size === 0) {
      mostrarToast('No hay cambios que guardar');
      return;
    }
    setGuardando(true);
    try {
      const payload = cables
        .filter(c => sucio.has(c.id))
        .map(c => ({
          id: c.id,
          nombre: c.nombre,
          precio_aparente: Number(c.precio_aparente) || 0,
          precio_tuberia_guiada: Number(c.precio_tuberia_guiada) || 0,
          precio_tuberia_no_guiada: Number(c.precio_tuberia_no_guiada) || 0,
          precio_tuberia_saturada: Number(c.precio_tuberia_saturada) || 0,
          orden: Number(c.orden) || 0,
        }));
      const r = await fetch(`${API_BASE_CLI}/api/catalogo/cableado-batch`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cables: payload }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      mostrarToast(`✅ ${r.actualizados} cable(s) actualizado(s)`);
      cargar();
    } catch (e) {
      mostrarToast('⚠️ ' + e.message);
    } finally {
      setGuardando(false);
    }
  };

  const desactivar = async (cable) => {
    if (!window.confirm(`¿Desactivar "${cable.nombre}"?`)) return;
    try {
      const r = await fetch(`${API_BASE_CLI}/api/catalogo/cableado/${cable.id}`, { method: 'DELETE', headers: { ...authHeaders() } }).then(x => x.json());
      if (r.ok) { mostrarToast('✅ Desactivado'); cargar(); }
      else mostrarToast('⚠️ ' + (r.error || 'error'));
    } catch (e) { mostrarToast('⚠️ ' + e.message); }
  };

  return (
    <div>
      <div style={{ ...estiloTarjeta, marginBottom: 14, padding: 14 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 4 }}>
          <Cable size={16} strokeWidth={2} /> Cableado con niveles de dificultad
        </div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, lineHeight: 1.5 }}>
          Precio por metro · incluye material + mano de obra. Los 4 niveles representan la complejidad
          del tendido (desde cable a la vista hasta canalización saturada).
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <button style={estiloBotonPrimario} onClick={() => setModalNuevo(true)}>
          + Nuevo tipo de cable
        </button>
        <button
          style={{ ...estiloBotonPrimario, background: '#1D9E75', opacity: sucio.size === 0 || guardando ? 0.6 : 1 }}
          disabled={sucio.size === 0 || guardando}
          onClick={guardarTodo}
        >
          {guardando ? 'Guardando…' : sucio.size > 0 ? `💾 Guardar cambios (${sucio.size})` : '💾 Guardar cambios'}
        </button>
        {seleccionados.size > 0 && (
          <button
            style={{ ...estiloBotonPrimario, background: '#7c3aed' }}
            onClick={() => setModalBatch(true)}
          >
            📋 Agregar {seleccionados.size} a lista pactada
          </button>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 12, color: COLOR_GRIS_CLARO }}>
          {cargando ? 'Cargando…' : `${cables.length} tipo(s) de cable · ${seleccionados.size} seleccionado(s)`}
        </div>
      </div>

      <div style={{ ...estiloTarjeta, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: COLOR_FONDO }}>
              <tr>
                <th style={{ ...th, width: 36, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={cables.length > 0 && seleccionados.size === cables.length}
                    onChange={(e) => {
                      if (e.target.checked) setSeleccionados(new Set(cables.map(c => c.id)));
                      else setSeleccionados(new Set());
                    }}
                    title="Seleccionar todos"
                  />
                </th>
                <th style={th}>Tipo de cable</th>
                {NIVEL_CABLEADO.map(n => (
                  <th key={n.id} style={{ ...th, textAlign: 'right' }}>
                    <PuntoNivel color={n.color} /> {n.label}
                  </th>
                ))}
                <th style={{ ...th, textAlign: 'right', width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {cables.length === 0 && !cargando && (
                <tr>
                  <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO }}>
                    Sin cables. Empieza con <strong>+ Nuevo tipo de cable</strong>.
                  </td>
                </tr>
              )}
              {cables.map(c => {
                const sel = seleccionados.has(c.id);
                return (
                  <tr key={c.id} style={{
                    borderTop: `1px solid ${COLOR_BORDE}`,
                    background: sel ? '#eef4fc' : sucio.has(c.id) ? '#fffbe6' : 'transparent',
                    opacity: c.activo ? 1 : 0.55,
                  }}>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={sel}
                        onChange={() => {
                          setSeleccionados(s => {
                            const n = new Set(s);
                            if (n.has(c.id)) n.delete(c.id); else n.add(c.id);
                            return n;
                          });
                        }}
                      />
                    </td>
                    <td style={td}>
                      <input
                        style={{ ...estiloInput, padding: '6px 8px', fontSize: 13 }}
                        value={c.nombre || ''}
                        onChange={(e) => cambiar(c.id, 'nombre', e.target.value)}
                      />
                    </td>
                    {NIVEL_CABLEADO.map(n => (
                      <td key={n.id} style={{ ...td, width: 120 }}>
                        <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', left: 8, top: 7, color: COLOR_GRIS_CLARO, fontSize: 12 }}>$</span>
                          <CampoPrecio
                            style={{ ...estiloInput, padding: '6px 8px 6px 16px', fontSize: 13, textAlign: 'right' }}
                            value={c[n.id] ?? ''}
                            onChange={(e) => cambiar(c.id, n.id, e.target.value)}
                          />
                        </div>
                      </td>
                    ))}
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={btnIcon} onClick={() => desactivar(c)} title="Desactivar"><Trash2 size={16} strokeWidth={2} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalNuevo && (
        <CableNuevoForm
          onCerrar={() => setModalNuevo(false)}
          onGuardado={() => {
            setModalNuevo(false);
            mostrarToast('✅ Cable creado');
            cargar();
          }}
          ordenSiguiente={(cables.reduce((mx, c) => Math.max(mx, c.orden || 0), 0) + 1)}
        />
      )}

      {modalBatch && (
        <ModalBatchCables
          cables={cables.filter(c => seleccionados.has(c.id))}
          onCerrar={() => setModalBatch(false)}
          onGuardado={(resumen) => {
            setModalBatch(false);
            setSeleccionados(new Set());
            mostrarToast(`✅ ${resumen.items_creados} agregados · ${resumen.items_existentes} actualizados`);
          }}
        />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c', color: '#fff', padding: '10px 16px', borderRadius: 10,
          fontSize: 12, zIndex: 1500, fontFamily: PP,
        }}>{toast}</div>
      )}
    </div>
  );
}

function CableNuevoForm({ onCerrar, onGuardado, ordenSiguiente }) {
  const [form, setForm] = useState({
    nombre: '',
    precio_aparente: '',
    precio_tuberia_guiada: '',
    precio_tuberia_no_guiada: '',
    precio_tuberia_saturada: '',
    orden: String(ordenSiguiente || 0),
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const guardar = async () => {
    if (!form.nombre.trim()) { setError('Nombre requerido'); return; }
    setError(null);
    setGuardando(true);
    try {
      const body = {
        nombre: form.nombre.trim(),
        precio_aparente: Number(form.precio_aparente) || 0,
        precio_tuberia_guiada: Number(form.precio_tuberia_guiada) || 0,
        precio_tuberia_no_guiada: Number(form.precio_tuberia_no_guiada) || 0,
        precio_tuberia_saturada: Number(form.precio_tuberia_saturada) || 0,
        orden: Number(form.orden) || 0,
      };
      const r = await fetch(`${API_BASE_CLI}/api/catalogo/cableado`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      onGuardado && onGuardado(r.cable);
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
          fontFamily: PP, color: COLOR_GRIS, padding: 24,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 18 }}>
          Nuevo tipo de cable
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={lbl}>Nombre *</div>
          <input
            style={estiloInput}
            value={form.nombre}
            onChange={(e) => setForm(f => ({ ...f, nombre: e.target.value }))}
            placeholder="Ej. Cable UTP Cat6"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          {NIVEL_CABLEADO.map(n => (
            <div key={n.id}>
              <div style={lbl}><PuntoNivel color={n.color} /> {n.label}</div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: 9, color: COLOR_GRIS_CLARO, fontSize: 13 }}>$</span>
                <CampoPrecio
                  style={{ ...estiloInput, paddingLeft: 22 }}
                  value={form[n.id]}
                  onChange={(e) => setForm(f => ({ ...f, [n.id]: e.target.value }))}
                />
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={lbl}>Orden</div>
          <input
            style={estiloInput}
            type="number"
            value={form.orden}
            onChange={(e) => setForm(f => ({ ...f, orden: e.target.value }))}
          />
        </div>

        {error && (
          <div style={{ marginBottom: 12, padding: 10, background: '#fde8e8', borderRadius: 8, fontSize: 12, color: '#8e3838' }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button
            style={{ ...estiloBotonPrimario, opacity: guardando ? 0.6 : 1 }}
            onClick={guardar}
            disabled={guardando}
          >{guardando ? 'Guardando…' : 'Crear cable'}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Modal BATCH CABLES — pactar múltiples cables con sus 4 niveles
// ============================================

function ModalBatchCables({ cables, onCerrar, onGuardado }) {
  const [listas, setListas] = useState([]);
  const [listaId, setListaId] = useState('');
  // precios[cableId] = { aparente, guiada, no_guiada, saturada }
  const [precios, setPrecios] = useState(() => {
    const init = {};
    for (const c of cables) {
      init[c.id] = {
        aparente: '',
        guiada: '',
        no_guiada: '',
        saturada: '',
      };
    }
    return init;
  });
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE_CLI}/api/listas`, { headers: authHeaders() }).then(x => x.json());
        if (r.ok) {
          const activas = (r.listas || []).filter(l => l.activa);
          setListas(activas);
          if (activas.length > 0) setListaId(String(activas[0].id));
        }
      } finally { setCargando(false); }
    })();
  }, []);

  const setPrecio = (cableId, nivel, valor) => {
    setPrecios(p => ({
      ...p,
      [cableId]: { ...(p[cableId] || {}), [nivel]: valor },
    }));
  };

  const CAMPO = {
    aparente: 'precio_aparente',
    guiada: 'precio_tuberia_guiada',
    no_guiada: 'precio_tuberia_no_guiada',
    saturada: 'precio_tuberia_saturada',
  };

  const aplicarDescuentoGlobal = (pct) => {
    const nuevo = {};
    for (const c of cables) {
      nuevo[c.id] = {
        aparente: String(((Number(c.precio_aparente) || 0) * (1 - pct / 100)).toFixed(2)),
        guiada: String(((Number(c.precio_tuberia_guiada) || 0) * (1 - pct / 100)).toFixed(2)),
        no_guiada: String(((Number(c.precio_tuberia_no_guiada) || 0) * (1 - pct / 100)).toFixed(2)),
        saturada: String(((Number(c.precio_tuberia_saturada) || 0) * (1 - pct / 100)).toFixed(2)),
      };
    }
    setPrecios(nuevo);
  };

  const contarItems = () => {
    let n = 0;
    for (const c of cables) {
      for (const nv of ['aparente', 'guiada', 'no_guiada', 'saturada']) {
        const v = Number(precios[c.id]?.[nv]);
        if (Number.isFinite(v) && v > 0) n++;
      }
    }
    return n;
  };

  const guardar = async () => {
    if (!listaId) { setError('Selecciona una lista'); return; }
    const payload = [];
    for (const c of cables) {
      const pp = {};
      for (const nv of ['aparente', 'guiada', 'no_guiada', 'saturada']) {
        const v = Number(precios[c.id]?.[nv]);
        if (Number.isFinite(v) && v > 0) pp[nv] = v;
      }
      if (Object.keys(pp).length > 0) payload.push({ cable_id: c.id, precios_pactados: pp });
    }
    if (payload.length === 0) { setError('Llena al menos un precio'); return; }
    setError(null);
    setGuardando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/listas/${listaId}/items/batch-cables`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cables: payload }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      onGuardado && onGuardado(r);
    } catch (e) { setError(e.message); } finally { setGuardando(false); }
  };

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1250,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 760,
          fontFamily: PP, color: COLOR_GRIS, maxHeight: 'calc(var(--beep-vh) * 92)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${COLOR_BORDE}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO }}>
            Agregar {cables.length} cable(s) a lista pactada
          </div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
            Define un precio por cada nivel donde quieras pactar. Los niveles en blanco se omiten.
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 24 }}>
          {cargando ? (
            <div style={{ color: COLOR_GRIS_CLARO }}>Cargando listas…</div>
          ) : listas.length === 0 ? (
            <div style={{ padding: 16, background: COLOR_FONDO, borderRadius: 8, fontSize: 13 }}>
              No hay listas activas. Crea una desde <strong>Listas pactadas</strong> primero.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={lbl}>Lista destino</div>
                <select style={estiloInput} value={listaId} onChange={(e) => setListaId(e.target.value)}>
                  {listas.map(l => <option key={l.id} value={l.id}>📋 {l.nombre}</option>)}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                <button style={estiloBotonSecundario} onClick={() => aplicarDescuentoGlobal(10)}>
                  Aplicar 10% descuento global
                </button>
                <button style={estiloBotonSecundario} onClick={() => aplicarDescuentoGlobal(15)}>
                  15%
                </button>
                <button style={estiloBotonSecundario} onClick={() => aplicarDescuentoGlobal(20)}>
                  20%
                </button>
                <button style={estiloBotonSecundario} onClick={() => aplicarDescuentoGlobal(0)}>
                  Copiar precios estándar
                </button>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                {cables.map(c => (
                  <div key={c.id} style={{ ...estiloTarjeta, padding: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 4 }}>
                      {c.nombre}
                    </div>
                    <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginBottom: 10 }}>
                      Estándar: ${fmtNumDinero(c.precio_aparente)} / ${fmtNumDinero(c.precio_tuberia_guiada)} / ${fmtNumDinero(c.precio_tuberia_no_guiada)} / ${fmtNumDinero(c.precio_tuberia_saturada)}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                      {NIVEL_CABLEADO.map(n => {
                        const key = n.id.replace('precio_', '').replace('tuberia_', '');
                        const shortKey = key === 'aparente' ? 'aparente'
                          : key === 'guiada' ? 'guiada'
                          : key === 'no_guiada' ? 'no_guiada'
                          : 'saturada';
                        const costo = Number(c[CAMPO[shortKey]]) || 0;
                        const valor = precios[c.id]?.[shortKey] ?? '';
                        const pactado = Number(valor);
                        const margen = costo > 0 && Number.isFinite(pactado)
                          ? ((pactado - costo) / costo) * 100 : null;
                        const mColor = margen == null ? COLOR_GRIS_CLARO
                          : margen >= 25 ? '#16a34a'
                          : margen >= 15 ? '#f59e0b' : '#e05c5c';
                        return (
                          <div key={n.id}>
                            <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, marginBottom: 3 }}>
                              <PuntoNivel color={n.color} /> {n.label}
                            </div>
                            <div style={{ position: 'relative' }}>
                              <span style={{ position: 'absolute', left: 8, top: 6, color: COLOR_GRIS_CLARO, fontSize: 11 }}>$</span>
                              <CampoPrecio
                                style={{ ...estiloInput, padding: '5px 6px 5px 16px', fontSize: 12, textAlign: 'right' }}
                                placeholder={`std ${costo}`}
                                value={valor}
                                onChange={(e) => setPrecio(c.id, shortKey, e.target.value)}
                              />
                            </div>
                            {margen != null && (
                              <div style={{ fontSize: 10, color: mColor, marginTop: 2, fontWeight: 600 }}>
                                {margen.toFixed(0)}%
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {error && (
                <div style={{ marginTop: 14, padding: 10, background: '#fde8e8', borderRadius: 8, fontSize: 12, color: '#8e3838' }}>
                  ⚠️ {error}
                </div>
              )}
            </>
          )}
        </div>
        <div style={{
          padding: '14px 24px', borderTop: `1px solid ${COLOR_BORDE}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>
            {contarItems()} item(s) a crear
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={estiloBotonSecundario} onClick={onCerrar} disabled={guardando}>Cancelar</button>
            {listas.length > 0 && (
              <button
                style={{ ...estiloBotonPrimario, opacity: guardando ? 0.6 : 1 }}
                onClick={guardar}
                disabled={guardando}
              >
                {guardando ? 'Guardando…' : `Agregar ${contarItems()} items a lista`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// TAB 2: MANO DE OBRA — matriz 2x4 por categoría + precios fijos
// ============================================

function TabManoObra() {
  const { formatear } = useMoneda();
  const fmt = (n) => formatear(n, 'MXN');
  const [categorias, setCategorias] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [modalPrecios, setModalPrecios] = useState(null);
  const [modalAgregar, setModalAgregar] = useState(null); // solo para fijos
  const [modalPaquete, setModalPaquete] = useState(null);  // categoría con niveles
  const [toast, setToast] = useState(null);

  const mostrarToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(null), 2500);
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/catalogo/mano-obra`).then(x => x.json());
      if (r.ok) setCategorias(r.categorias || []);
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const conNiveles = categorias.filter(c => c.tiene_niveles);
  const sinNiveles = categorias.filter(c => !c.tiene_niveles);

  return (
    <div>
      {cargando && <div style={{ color: COLOR_GRIS_CLARO }}>Cargando…</div>}

      {conNiveles.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>
            Instalaciones y servicios (con niveles)
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
            gap: 12,
          }}>
            {conNiveles.map(cat => (
              <TarjetaCategoriaNiveles
                key={cat.id}
                cat={cat}
                fmt={fmt}
                onEditar={() => setModalPrecios(cat)}
                onPactarPaquete={() => setModalPaquete(cat)}
              />
            ))}
          </div>
        </div>
      )}

      {sinNiveles.length > 0 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>
            Otros servicios (precio fijo)
          </div>
          <div style={{ ...estiloTarjeta, padding: 0, overflow: 'hidden' }}>
            {sinNiveles.map((cat, i) => (
              <div key={cat.id} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                borderTop: i === 0 ? 'none' : `1px solid ${COLOR_BORDE}`,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: (cat.color || COLOR_PRINCIPAL) + '22',
                  color: cat.color || COLOR_PRINCIPAL,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22,
                }}><IconoManoObra nombre={cat.nombre} size={24} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>{cat.nombre}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {cat.precios.fijo == null || cat.precios.fijo === 0 ? (
                    <div style={{ fontSize: 12, fontStyle: 'italic', color: COLOR_GRIS_CLARO }}>
                      variable
                    </div>
                  ) : (
                    <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO }}>
                      {fmt(cat.precios.fijo)}
                    </div>
                  )}
                </div>
                <button
                  style={estiloBotonSecundario}
                  onClick={() => setModalAgregar({
                    nombre_display: cat.nombre,
                    tipo: 'beep_fijo',
                    referencia_id: `${cat.id}:`,
                    precio_base_mxn: Number(cat.precios.fijo) || 0,
                  })}
                  title="Agregar a lista pactada"
                >📋+</button>
                <button
                  style={estiloBotonSecundario}
                  onClick={() => setModalPrecios(cat)}
                >✏️ Editar</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!cargando && categorias.length === 0 && (
        <div style={{ ...estiloTarjeta, padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO }}>
          Sin categorías. Usa el endpoint <code>POST /api/catalogo/reset-definitivo</code> para inicializar.
        </div>
      )}

      {modalPrecios && (
        <ModalEditarPrecios
          categoria={modalPrecios}
          onCerrar={() => setModalPrecios(null)}
          onGuardado={() => {
            mostrarToast('✅ Precios actualizados');
            cargar();
          }}
        />
      )}

      {modalAgregar && (
        <AgregarAListaModal
          item={modalAgregar}
          onCerrar={() => setModalAgregar(null)}
          onGuardado={() => {
            setModalAgregar(null);
            mostrarToast('✅ Agregado a lista pactada');
          }}
        />
      )}

      {modalPaquete && (
        <ModalBatchManoObra
          categoria={modalPaquete}
          onCerrar={() => setModalPaquete(null)}
          onGuardado={(resumen) => {
            setModalPaquete(null);
            mostrarToast(`✅ ${resumen.items_creados} agregados · ${resumen.items_existentes} actualizados`);
          }}
        />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c', color: '#fff', padding: '10px 16px', borderRadius: 10,
          fontSize: 12, zIndex: 1500, fontFamily: PP,
        }}>{toast}</div>
      )}
    </div>
  );
}

function TarjetaCategoriaNiveles({ cat, onEditar, onPactarPaquete, fmt }) {
  const color = cat.color || COLOR_PRINCIPAL;
  return (
    <div style={{ ...estiloTarjeta, padding: 16, borderTop: `4px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10,
          background: color + '22', color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24,
        }}><IconoManoObra nombre={cat.nombre} size={24} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OSCURO }}>{cat.nombre}</div>
        </div>
        {onPactarPaquete && (
          <button
            style={{ ...estiloBotonPrimario, background: '#7c3aed' }}
            onClick={onPactarPaquete}
            title="Pactar como paquete a una lista"
          ><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><ClipboardList size={14} strokeWidth={2} /> Pactar como paquete</span></button>
        )}
        <button style={estiloBotonSecundario} onClick={onEditar}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Pencil size={14} strokeWidth={2} /> Editar precios</span></button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ ...th, padding: '8px 6px' }}></th>
              {NIVEL_INSTALACION.map(n => (
                <th key={n.id} style={{ ...th, padding: '8px 6px', textAlign: 'center' }}>
                  <PuntoNivel color={n.color} /> {n.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderTop: `1px solid ${COLOR_BORDE}` }}>
              <td style={{ ...td, fontWeight: 600, color: COLOR_OSCURO, padding: '8px 6px' }}>Instalación</td>
              {NIVEL_INSTALACION.map(n => (
                <td key={n.id} style={{ ...td, textAlign: 'center', padding: '8px 6px', fontWeight: 600 }}>
                  {fmt(cat.precios.instalacion[n.id])}
                </td>
              ))}
            </tr>
            <tr style={{ borderTop: `1px solid ${COLOR_BORDE}` }}>
              <td style={{ ...td, fontWeight: 600, color: COLOR_OSCURO, padding: '8px 6px' }}>Servicio</td>
              {NIVEL_INSTALACION.map(n => (
                <td key={n.id} style={{ ...td, textAlign: 'center', padding: '8px 6px', fontWeight: 600 }}>
                  {fmt(cat.precios.servicio[n.id])}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================
// Modal BATCH MANO DE OBRA — pactar 8 precios de una categoría en un paso
// ============================================

function ModalBatchManoObra({ categoria, onCerrar, onGuardado }) {
  const [listas, setListas] = useState([]);
  const [listaId, setListaId] = useState('');
  const [inst, setInst] = useState({ basica: '', estandar: '', altura: '', compleja: '' });
  const [serv, setServ] = useState({ basica: '', estandar: '', altura: '', compleja: '' });
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE_CLI}/api/listas`, { headers: authHeaders() }).then(x => x.json());
        if (r.ok) {
          const activas = (r.listas || []).filter(l => l.activa);
          setListas(activas);
          if (activas.length > 0) setListaId(String(activas[0].id));
        }
      } finally { setCargando(false); }
    })();
  }, []);

  const aplicarDescuento = (pct) => {
    const i = {}; const s = {};
    for (const nv of ['basica', 'estandar', 'altura', 'compleja']) {
      const vi = Number(categoria.precios.instalacion[nv]) || 0;
      const vs = Number(categoria.precios.servicio[nv]) || 0;
      i[nv] = String((vi * (1 - pct / 100)).toFixed(2));
      s[nv] = String((vs * (1 - pct / 100)).toFixed(2));
    }
    setInst(i);
    setServ(s);
  };

  const mSect = (stdVal, pactVal) => {
    const costo = Number(stdVal) || 0;
    const p = Number(pactVal);
    if (costo <= 0 || !Number.isFinite(p)) return null;
    return ((p - costo) / costo) * 100;
  };
  const mColor = (m) => m == null ? COLOR_GRIS_CLARO : m >= 25 ? '#16a34a' : m >= 15 ? '#f59e0b' : '#e05c5c';
  const mIcono = (m) => m == null ? '' : m >= 25 ? '🟢' : m >= 15 ? '🟡' : '🔴';

  const margenes = useMemo(() => {
    const arr = [];
    for (const nv of ['basica', 'estandar', 'altura', 'compleja']) {
      const mi = mSect(categoria.precios.instalacion[nv], inst[nv]);
      const ms = mSect(categoria.precios.servicio[nv], serv[nv]);
      if (mi != null) arr.push(mi);
      if (ms != null) arr.push(ms);
    }
    if (arr.length === 0) return { promedio: null };
    return { promedio: arr.reduce((a, b) => a + b, 0) / arr.length };
    // eslint-disable-next-line
  }, [inst, serv, categoria]);

  const contar = () => {
    let n = 0;
    for (const nv of ['basica', 'estandar', 'altura', 'compleja']) {
      if (Number.isFinite(Number(inst[nv])) && Number(inst[nv]) > 0) n++;
      if (Number.isFinite(Number(serv[nv])) && Number(serv[nv]) > 0) n++;
    }
    return n;
  };

  const guardar = async () => {
    if (!listaId) { setError('Selecciona una lista'); return; }
    const armar = (obj) => {
      const out = {};
      for (const nv of ['basica', 'estandar', 'altura', 'compleja']) {
        const v = Number(obj[nv]);
        if (Number.isFinite(v) && v > 0) out[nv] = v;
      }
      return out;
    };
    const instFinal = armar(inst);
    const servFinal = armar(serv);
    if (Object.keys(instFinal).length === 0 && Object.keys(servFinal).length === 0) {
      setError('Llena al menos un precio');
      return;
    }
    setError(null);
    setGuardando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/listas/${listaId}/items/batch-mano-obra`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categoria_id: categoria.id,
          instalacion: Object.keys(instFinal).length > 0 ? instFinal : undefined,
          servicio: Object.keys(servFinal).length > 0 ? servFinal : undefined,
        }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      onGuardado && onGuardado(r);
    } catch (e) { setError(e.message); } finally { setGuardando(false); }
  };

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1250,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560,
          fontFamily: PP, color: COLOR_GRIS, maxHeight: 'calc(var(--beep-vh) * 92)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${COLOR_BORDE}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconoManoObra nombre={categoria.nombre} size={18} /> Pactar paquete: {categoria.nombre}
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 24 }}>
          {cargando ? (
            <div style={{ color: COLOR_GRIS_CLARO }}>Cargando listas…</div>
          ) : listas.length === 0 ? (
            <div style={{ padding: 16, background: COLOR_FONDO, borderRadius: 8, fontSize: 13 }}>
              No hay listas activas. Crea una desde <strong>Listas pactadas</strong> primero.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={lbl}>Lista destino</div>
                <select style={estiloInput} value={listaId} onChange={(e) => setListaId(e.target.value)}>
                  {listas.map(l => <option key={l.id} value={l.id}>📋 {l.nombre}</option>)}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                <button style={estiloBotonSecundario} onClick={() => aplicarDescuento(10)}>−10%</button>
                <button style={estiloBotonSecundario} onClick={() => aplicarDescuento(15)}>−15%</button>
                <button style={estiloBotonSecundario} onClick={() => aplicarDescuento(20)}>−20%</button>
                <button style={estiloBotonSecundario} onClick={() => aplicarDescuento(0)}>Copiar precios estándar</button>
              </div>

              <BlockMO
                titulo="INSTALACIÓN"
                precios={inst}
                stdPrecios={categoria.precios.instalacion}
                onChange={(nv, v) => setInst(s => ({ ...s, [nv]: v }))}
                mColor={mColor} mIcono={mIcono} mSect={mSect}
              />
              <div style={{ height: 14 }} />
              <BlockMO
                titulo="SERVICIO"
                precios={serv}
                stdPrecios={categoria.precios.servicio}
                onChange={(nv, v) => setServ(s => ({ ...s, [nv]: v }))}
                mColor={mColor} mIcono={mIcono} mSect={mSect}
              />

              {margenes.promedio != null && (
                <div style={{
                  marginTop: 14, padding: '10px 12px', borderRadius: 8,
                  background: COLOR_FONDO, fontSize: 13, color: COLOR_OSCURO,
                }}>
                  Margen promedio del paquete: <strong style={{ color: mColor(margenes.promedio) }}>
                    {margenes.promedio.toFixed(1)}% {mIcono(margenes.promedio)}
                  </strong>
                </div>
              )}

              {error && (
                <div style={{ marginTop: 12, padding: 10, background: '#fde8e8', borderRadius: 8, fontSize: 12, color: '#8e3838' }}>
                  ⚠️ {error}
                </div>
              )}
            </>
          )}
        </div>
        <div style={{
          padding: '14px 24px', borderTop: `1px solid ${COLOR_BORDE}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button style={estiloBotonSecundario} onClick={onCerrar} disabled={guardando}>Cancelar</button>
          {listas.length > 0 && (
            <button
              style={{ ...estiloBotonPrimario, opacity: guardando ? 0.6 : 1 }}
              onClick={guardar}
              disabled={guardando}
            >
              {guardando ? 'Guardando…' : `Guardar paquete (${contar()} items)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function BlockMO({ titulo, precios, stdPrecios, onChange, mColor, mIcono, mSect }) {
  const niveles = [
    { id: 'basica', label: 'Básica', icono: '🟢' },
    { id: 'estandar', label: 'Estándar', icono: '🔵' },
    { id: 'altura', label: 'Altura', icono: '🟡' },
    { id: 'compleja', label: 'Compleja', icono: '🔴' },
  ];
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_OSCURO, letterSpacing: 0.3, marginBottom: 8 }}>
        {titulo}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {niveles.map(n => {
          const std = Number(stdPrecios[n.id]) || 0;
          const pact = precios[n.id];
          const m = mSect(std, pact);
          return (
            <div key={n.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 120px', gap: 10, alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: COLOR_GRIS }}>
                <PuntoNivel color={n.color} /> {n.label} <span style={{ color: COLOR_GRIS_CLARO }}>${std}</span> →
              </div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: 9, color: COLOR_GRIS_CLARO, fontSize: 13 }}>$</span>
                <CampoPrecio
                  style={{ ...estiloInput, paddingLeft: 22 }}
                  placeholder={`std ${std}`}
                  value={pact}
                  onChange={(e) => onChange(n.id, e.target.value)}
                />
              </div>
              <div style={{ fontSize: 12, color: mColor(m), fontWeight: 600, textAlign: 'right' }}>
                {m != null ? `${m.toFixed(0)}% ${mIcono(m)}` : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModalEditarPrecios({ categoria, onCerrar, onGuardado }) {
  const tieneNiveles = !!categoria.tiene_niveles;

  const estadoInicial = useMemo(() => {
    const toStr = (obj) => Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v != null ? String(v) : ''])
    );
    if (tieneNiveles) {
      const blank = { basica: '', estandar: '', altura: '', compleja: '' };
      const inst = { ...blank, ...(categoria.precios?.instalacion || {}) };
      const serv = { ...blank, ...(categoria.precios?.servicio || {}) };
      return { instalacion: toStr(inst), servicio: toStr(serv), fijo: '' };
    }
    return {
      instalacion: {},
      servicio: {},
      fijo: categoria.precios?.fijo != null ? String(categoria.precios.fijo) : '',
    };
  }, [categoria, tieneNiveles]);

  const [form, setForm] = useState(estadoInicial);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const setNivel = (tipo, nivel, valor) => {
    setForm(f => ({ ...f, [tipo]: { ...f[tipo], [nivel]: valor } }));
  };

  const guardar = async () => {
    setError(null);
    setGuardando(true);
    try {
      const body = {};
      if (tieneNiveles) {
        body.instalacion = {};
        body.servicio = {};
        for (const nv of NIVEL_INSTALACION) {
          const i = Number(form.instalacion[nv.id]);
          const s = Number(form.servicio[nv.id]);
          if (!Number.isFinite(i) || i < 0) throw new Error(`Instalación ${nv.label} inválida`);
          if (!Number.isFinite(s) || s < 0) throw new Error(`Servicio ${nv.label} inválido`);
          body.instalacion[nv.id] = i;
          body.servicio[nv.id] = s;
        }
      } else {
        const p = Number(form.fijo);
        if (!Number.isFinite(p) || p < 0) throw new Error('Precio fijo inválido');
        body.fijo = p;
      }
      const r = await fetch(`${API_BASE_CLI}/api/catalogo/mano-obra/${categoria.id}/precios`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      onGuardado && onGuardado(r);
      onCerrar && onCerrar();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
          fontFamily: PP, color: COLOR_GRIS, maxHeight: 'calc(var(--beep-vh) * 92)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${COLOR_BORDE}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconoManoObra nombre={categoria.nombre} size={18} /> Editar precios de {categoria.nombre}
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 24 }}>
          {tieneNiveles ? (
            <>
              <SeccionNiveles
                titulo="Instalación"
                valores={form.instalacion}
                onCambio={(nv, v) => setNivel('instalacion', nv, v)}
              />
              <div style={{ height: 16 }} />
              <SeccionNiveles
                titulo="Servicio"
                valores={form.servicio}
                onCambio={(nv, v) => setNivel('servicio', nv, v)}
              />
            </>
          ) : (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Precio fijo
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={lbl}>Precio</div>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: 9, color: COLOR_GRIS_CLARO }}>$</span>
                  <CampoPrecio
                    style={{ ...estiloInput, paddingLeft: 22 }}
                    value={form.fijo}
                    onChange={(e) => setForm(f => ({ ...f, fijo: e.target.value }))}
                  />
                </div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
                  Si el precio se cotiza por proyecto, usa 0 y se mostrará como "variable".
                </div>
              </div>
            </div>
          )}

          {error && (
            <div style={{ marginTop: 12, padding: 10, background: '#fde8e8', borderRadius: 8, fontSize: 12, color: '#8e3838' }}>
              ⚠️ {error}
            </div>
          )}
        </div>

        <div style={{
          padding: '14px 24px', borderTop: `1px solid ${COLOR_BORDE}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button style={estiloBotonSecundario} onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button
            style={{ ...estiloBotonPrimario, opacity: guardando ? 0.6 : 1 }}
            onClick={guardar}
            disabled={guardando}
          >{guardando ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

function SeccionNiveles({ titulo, valores, onCambio }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {titulo}
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {NIVEL_INSTALACION.map(n => (
          <div key={n.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10, alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>
              <PuntoNivel color={n.color} /> {n.label}:
            </div>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: 9, color: COLOR_GRIS_CLARO }}>$</span>
              <CampoPrecio
                style={{ ...estiloInput, paddingLeft: 22 }}
                value={valores[n.id] ?? ''}
                onChange={(e) => onCambio(n.id, e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// ESTILOS CHICOS
// ============================================

const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: COLOR_GRIS, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 };
const td = { padding: '8px 10px', verticalAlign: 'middle', color: COLOR_GRIS };
const btnIcon = { background: 'transparent', border: 'none', fontSize: 16, cursor: 'pointer', padding: '4px 6px' };
const lbl = { fontSize: 11, fontWeight: 600, color: COLOR_GRIS, marginBottom: 4 };
